/**
 * Tabs telling each other what they saved.
 *
 * The replacement for the server's WebSocket, with the same discipline: a
 * message carries a version number, never a document. The document always
 * travels through storage and the merge (`pullAndIntegrate`), so a lost message
 * costs one catch-up on the next focus, never a wrong canvas.
 *
 * `BroadcastChannel` does not deliver a message to the object that posted it,
 * and this module keeps one object per tab — so a tab never hears its own saves.
 */

export type TabMessage =
  | { type: 'saved'; planId: string; version: number }
  | { type: 'deleted'; planId: string }

const CHANNEL_NAME = 'yerbeyer'

let channel: BroadcastChannel | null = null
const listeners = new Set<(message: TabMessage) => void>()

function ensureChannel(): BroadcastChannel | null {
  if (channel) return channel
  if (typeof BroadcastChannel === 'undefined') return null
  channel = new BroadcastChannel(CHANNEL_NAME)
  channel.onmessage = (event: MessageEvent<TabMessage>) => {
    for (const listener of listeners) listener(event.data)
  }
  return channel
}

export function announce(message: TabMessage): void {
  try {
    ensureChannel()?.postMessage(message)
  } catch {
    /* best effort: other tabs catch up when they are next focused */
  }
}

export function onTabMessage(listener: (message: TabMessage) => void): () => void {
  ensureChannel()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Tests: drop the channel so one test's tab does not hear another's. */
export function closeTabChannel(): void {
  channel?.close()
  channel = null
  listeners.clear()
}
